import type { Passenger } from '@optionway/models';
import { isSupportedCountry, isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

const errorPrefix = 'components.passengers.form.errors';

export const passengerSchema = z.object({
  title: z.enum(['MR', 'MRS', 'MISS'], { required_error: `${errorPrefix}.civilityRequired` }),
  lastName: z.string({ required_error: `${errorPrefix}.lastNameRequired` }).min(1, `${errorPrefix}.lastNameRequired`),
  firstName: z
    .string({ required_error: `${errorPrefix}.firstNameRequired` })
    .min(1, `${errorPrefix}.firstNameRequired`),
  birthdate: z
    .string({ required_error: `${errorPrefix}.birthdateRequired` })
    .min(1, `${errorPrefix}.birthdateRequired`),
  nationality: z
    .object(
      {
        code: z
          .string({ required_error: `${errorPrefix}.nationalityRequired` })
          .min(1, `${errorPrefix}.nationalityRequired`),
      },
      { required_error: `${errorPrefix}.nationalityRequired` },
    )
    .passthrough(),
  contact: z
    .object({
      email: z
        .string({ required_error: `${errorPrefix}.emailRequired` })
        .min(1, `${errorPrefix}.emailRequired`)
        .email(`${errorPrefix}.emailInvalid`),
      phoneCode: z
        .object(
          {
            code: z
              .string({ required_error: `${errorPrefix}.phoneCodeRequired` })
              .min(1, `${errorPrefix}.phoneCodeRequired`),
          },
          { required_error: `${errorPrefix}.phoneCodeRequired` },
        )
        .passthrough(),
      phoneNumber: z
        .string({ required_error: `${errorPrefix}.phoneNumberRequired` })
        .min(1, `${errorPrefix}.phoneNumberRequired`),
    })
    .refine(
      (contact) => {
        if (!contact.phoneNumber || !contact.phoneCode.code) return true;
        if (!isSupportedCountry(contact.phoneCode.code)) return true;
        return isValidPhoneNumber(contact.phoneNumber, contact.phoneCode.code);
      },
      { message: `${errorPrefix}.phoneNumberInvalid`, path: ['phoneNumber'] },
    )
    .optional(),
});

export interface PassengerFormErrors {
  title?: string;
  lastName?: string;
  firstName?: string;
  birthdate?: string;
  nationality?: string;
  email?: string;
  phoneCode?: string;
  phoneNumber?: string;
}

const PATH_TO_KEY: Record<string, keyof PassengerFormErrors> = {
  title: 'title',
  lastName: 'lastName',
  firstName: 'firstName',
  birthdate: 'birthdate',
  nationality: 'nationality',
  'nationality.code': 'nationality',
  'contact.email': 'email',
  'contact.phoneCode': 'phoneCode',
  'contact.phoneCode.code': 'phoneCode',
  'contact.phoneNumber': 'phoneNumber',
};

export const mapPassengerZodErrors = (rawErrors: Record<string, string>): PassengerFormErrors =>
  pickFieldErrors(rawErrors, PATH_TO_KEY);

export const validatePassengerFields = (passenger: Passenger): PassengerFormErrors => {
  const result = passengerSchema.safeParse(passenger);
  return result.success ? {} : mapPassengerZodErrors(flattenZodIssues(result.error));
};

/** The structural errors of the form, with the two a schema cannot see folded in: the e-mail's
 *  deliverability, checked asynchronously, and the birthdate, which depends on the flight date.
 *  A structural error on a field wins — it is the one the traveller can act on first. */
export const passengerFieldErrors = (
  rawErrors: Record<string, string>,
  emailError?: string,
  birthdateError?: string,
): PassengerFormErrors => {
  const errors = mapPassengerZodErrors(rawErrors);
  if (emailError && !errors.email) {
    errors.email = emailError;
  }
  if (birthdateError && !errors.birthdate) {
    errors.birthdate = birthdateError;
  }
  return errors;
};
